import { lazy, Suspense, useEffect } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useAuthStore } from "@/stores/useAuthStore";
import { useIdleTimeout } from "@/shared/hooks/useIdleTimeout";
import { LockScreen } from "@/features/security/components/LockScreen";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { CanonicalRedirect } from "@/shared/seo/CanonicalRedirect";

// ============================================
// LAZY LOADED ROUTES — Code splitting por rota
// ============================================
const Index = lazy(() => import("@/pages/Index"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const Auth = lazy(() => import("@/pages/Auth"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Onboarding = lazy(() => import("@/pages/Onboarding"));
const Admin = lazy(() => import("@/pages/Admin"));
const Conciliacao = lazy(() => import("@/pages/Conciliacao"));
const AdminPublico = lazy(() => import("@/pages/AdminPublico"));
const AdminMetricas = lazy(() => import("@/pages/AdminMetricas"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Landing = lazy(() => import("@/pages/Landing"));
const Terms = lazy(() => import("@/pages/Terms"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const PartnerDocs = lazy(() => import("@/pages/PartnerDocs"));
const Governance = lazy(() => import("@/pages/Governance"));
const LaunchManual = lazy(() => import("@/pages/LaunchManual"));
const StoresPage = lazy(() => import("@/pages/StoresPage"));
const ReferralPage = lazy(() => import("@/pages/ReferralPage"));
const CEOi2 = lazy(() => import("@/pages/CEOi2"));
const Insights = lazy(() => import("@/pages/Insights"));
const NFPOnboarding = lazy(() => import("@/pages/NFPOnboarding"));
const SeoHealth = lazy(() => import("@/pages/SeoHealth"));
const AdminAuditLogs = lazy(() => import("@/pages/AdminAuditLogs"));
const SecurityReport = lazy(() => import("@/pages/SecurityReport"));
const CspViolations = lazy(() => import("@/pages/CspViolations"));
const LandingConversions = lazy(() => import("@/pages/LandingConversions"));
const LandingCtaRejections = lazy(() => import("@/pages/LandingCtaRejections"));
const AdminGoLive = lazy(() => import("@/pages/AdminGoLive"));
const AdminPilot = lazy(() => import("@/pages/AdminPilot"));
const AdminPartnerApiKeys = lazy(() => import("@/pages/AdminPartnerApiKeys"));
const AdminPartnerApiKeysTest = lazy(
  () => import("@/pages/AdminPartnerApiKeysTest"),
);
const AdminIncidents = lazy(() => import("@/pages/AdminIncidents"));
const AdminSecurityAudit = lazy(() => import("@/pages/AdminSecurityAudit"));
const AdminCeoi2Observability = lazy(
  () => import("@/pages/AdminCeoi2Observability"),
);
const AdminCeoi2Cost = lazy(() => import("@/pages/AdminCeoi2Cost"));
const AdminOnboarding = lazy(() => import("@/pages/AdminOnboarding"));
const AdminAudit = lazy(() => import("@/pages/AdminAudit"));
const AdminCeoi2Incidents = lazy(() => import("@/pages/AdminCeoi2Incidents"));
const AdminCeoi2AlertConfig = lazy(
  () => import("@/pages/AdminCeoi2AlertConfig"),
);
const AdminRpaControl = lazy(() => import("@/pages/AdminRpaControl"));
const BlogComoFuncionaNFP = lazy(() => import("@/pages/BlogComoFuncionaNFP"));
const BlogGuiaResgateNFP = lazy(() => import("@/pages/BlogGuiaResgateNFP"));
const ProcuracaoGovBr = lazy(() => import("@/pages/ProcuracaoGovBr"));
const CofreLocalPanel = lazy(() =>
  import("@/features/cofre/CofreLocalPanel").then((m) => ({
    default: m.CofreLocalPanel,
  })),
);
const ResgateNFP = lazy(() => import("@/pages/ResgateNFP"));
const OAuthConsent = lazy(() => import("@/pages/OAuthConsent"));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-3">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      <span className="text-sm text-muted-foreground">Carregando...</span>
    </div>
  </div>
);

export const AppRouter = () => {
  const { user, initialize } = useAuthStore();
  const location = useLocation();
  const { isLocked, unlock } = useIdleTimeout({
    timeoutSeconds: 180,
    enabled: !!user && location.pathname !== "/onboarding",
  });

  // Initialize Zustand auth store on mount
  useEffect(() => {
    const unsubscribe = initialize();
    return unsubscribe;
  }, [initialize]);

  return (
    <>
      {isLocked && user && (
        <LockScreen onUnlock={unlock} userEmail={user.email} />
      )}
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Landing />} />
          <Route path="/index" element={<CanonicalRedirect to="/" />} />
          <Route path="/home" element={<CanonicalRedirect to="/" />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/termos" element={<Terms />} />
          <Route path="/privacidade" element={<Privacy />} />
          <Route path="/partner-docs" element={<PartnerDocs />} />
          <Route path="/governanca" element={<Governance />} />
          <Route path="/manual-lancamento" element={<LaunchManual />} />
          <Route
            path="/blog/como-funciona-calculo-nfp"
            element={<BlogComoFuncionaNFP />}
          />
          <Route
            path="/blog/guia-resgate-nota-fiscal-paulista"
            element={<BlogGuiaResgateNFP />}
          />

          {/* Protected routes */}
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <Index />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <Onboarding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <Admin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/stores"
            element={
              <ProtectedRoute>
                <StoresPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/referral"
            element={
              <ProtectedRoute>
                <ReferralPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ceoi2"
            element={
              <ProtectedRoute>
                <CEOi2 />
              </ProtectedRoute>
            }
          />
          <Route
            path="/insights"
            element={
              <ProtectedRoute>
                <Insights />
              </ProtectedRoute>
            }
          />
          <Route
            path="/nfp/cadastro"
            element={
              <ProtectedRoute>
                <NFPOnboarding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/conciliacao"
            element={
              <ProtectedRoute>
                <Conciliacao />
              </ProtectedRoute>
            }
          />
          <Route
            path="/conciliação"
            element={
              <ProtectedRoute>
                <Conciliacao />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/publico"
            element={
              <ProtectedRoute>
                <AdminPublico />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/metricas"
            element={
              <ProtectedRoute>
                <AdminMetricas />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/seo-health"
            element={
              <ProtectedRoute>
                <SeoHealth />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/audit-logs"
            element={
              <ProtectedRoute>
                <AdminAuditLogs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/security-report"
            element={
              <ProtectedRoute>
                <SecurityReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/security-audit"
            element={
              <ProtectedRoute>
                <AdminSecurityAudit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/csp-violations"
            element={
              <ProtectedRoute>
                <CspViolations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/landing-conversions"
            element={
              <ProtectedRoute>
                <LandingConversions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/landing-cta-rejections"
            element={
              <ProtectedRoute>
                <LandingCtaRejections />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/go-live"
            element={
              <ProtectedRoute>
                <AdminGoLive />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/pilot"
            element={
              <ProtectedRoute>
                <AdminPilot />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/partner-api-keys"
            element={
              <ProtectedRoute>
                <AdminPartnerApiKeys />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/partner-api-keys/test"
            element={
              <ProtectedRoute>
                <AdminPartnerApiKeysTest />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/incidents"
            element={
              <ProtectedRoute>
                <AdminIncidents />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ceoi2-observability"
            element={
              <ProtectedRoute>
                <AdminCeoi2Observability />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ceoi2-cost"
            element={
              <ProtectedRoute>
                <AdminCeoi2Cost />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ceoi2-incidents"
            element={
              <ProtectedRoute>
                <AdminCeoi2Incidents />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ceoi2-incidents/:id"
            element={
              <ProtectedRoute>
                <AdminCeoi2Incidents />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/onboarding"
            element={
              <ProtectedRoute>
                <AdminOnboarding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/audit"
            element={
              <ProtectedRoute>
                <AdminAudit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ceoi2-alert-config"
            element={
              <ProtectedRoute>
                <AdminCeoi2AlertConfig />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/rpa-control"
            element={
              <ProtectedRoute>
                <AdminRpaControl />
              </ProtectedRoute>
            }
          />
          <Route
            path="/procuracao-govbr"
            element={
              <ProtectedRoute>
                <ProcuracaoGovBr />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cofre"
            element={
              <ProtectedRoute>
                <CofreLocalPanel />
              </ProtectedRoute>
            }
          />
          <Route
            path="/resgatar"
            element={
              <ProtectedRoute>
                <ResgateNFP />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
};
